import {
  CatalogLookupType,
  ChangeSetItem,
  ChangeSetItemType,
  UNCOVERED_CHANGE_SET_FIELDS,
  NUTRITION_UNCOVERED_FORM_KEYS,
  ChangesListResponse,
  FormattedChangesForItem,
  FormChangeForVersion,
  ItemChanges,
  PatchCatalogChangesInput,
  PatchCatalogChangesResponse,
} from '@interfaces/change-sets/1.0';
import { DataGroup } from '@interfaces/data-group/1.0';
import { Decorator, DecoratorType } from '@interfaces/decorator/1.0';
import { PosItem } from '@interfaces/pos-item/1.0';
import { MenuDataStatus } from '@services/menu/1.0/menu-data.service';
import { EMPTY, forkJoin, Observable, of, Subscription, throwError, timer } from 'rxjs';
import { catchError, first, map, mergeMap, takeUntil, takeWhile } from 'rxjs/operators';
import { TypeSelection } from '@components/type-selector/1.0';
import { ModifierGroup } from '@interfaces/modifier-group/1.0';
import { Tag } from '@interfaces/tags/1.0';
import { TagType } from '@generated/tcea/models';
import { UnsetService } from '@services/utilities/1.0/unset.service';
import { IsEmptyService } from '@services/utilities/1.0/is-empty.service';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { IsEqualService } from '@services/utilities/1.0/is-equal.service';
import { IsEqualWithService } from '@services/utilities/1.0/is-equal-with.service';
import { GetService } from '@services/utilities/1.0/get.service';
import { trimCatalogItemFields } from '@helpers/Validators';
import { OmitService } from '@services/utilities/1.0/omit.service';
import { UniqService } from '@services/utilities/1.0/uniq.service';
import { PickService } from '@services/utilities/1.0/pick.service';
import { ActivatedRoute, Router } from '@angular/router';
import { isLocalizedImageKey } from '@helpers/localized-image-key';

interface ModifierGroupLookupMapType {
  [modifierGroupId: string]: ModifierGroup;
}

export const CALORIC_COUNT_REMOVE_KEY = 'caloricCountInput';

export const catalogMixin = (Base) =>
  class extends Base {
    loadingDecorators: boolean;
    hasDecoratorLoadError: boolean;
    decorators: Decorator[];
    imageDecorators: Decorator[];
    decoratorSubscription: Subscription = EMPTY.subscribe();
    versionChangeTimerSubscription: Subscription = EMPTY.subscribe();
    versionChangeRequestId = 0;
    pendingDecoratorsCatalogEntryId: string;
    itemChangesCatalogEntryId: string;
    decoratorsCatalogEntryId: string;
    skuList: { [catalogEntryId: string]: string };
    loadingFields: string[] = [];
    dataGroup: DataGroup;
    itemChanges: ItemChanges;
    changeSets: ChangeSetItem[] = [];
    currentItemForChangeSet = null;
    cachedVersion: FormattedChangesForItem;
    imageFormValues: { [key: string]: string };
    selectedVersion: ChangeSetItemType;
    changesForChangeSet: { [key: string]: ChangesListResponse };
    lookupMapForChangeSetVersion: { [key: string]: ModifierGroupLookupMapType } = {};
    showModifierGroupWarning: boolean;
    cachedLookupMap: ModifierGroupLookupMapType;
    cachedBeforeVersionChange: { [key: string]: any } = {};
    cachedAfterVersionChange: { [key: string]: any } = {};
    /** Snapshot of draft decorators before itemChanges is updated during save. */
    draftDecoratorsBaselineForSave: Decorator[] | null = null;
    reloadItemFunction: any;
    typesOfContentMeasure: TypeSelection[];
    availableTags: Tag[] = [];
    tagAssociationsToBackup: any = {};
    isLoadingTags: boolean;
    isLoadingTagAssociations: boolean;
    marketingTagsFullList: Tag[] = [];
    tagAssociationList: any[] = [];
    tagLookup: { [tagId: string]: Tag } = {};
    showLoadTagsError: boolean;
    readonly nutritionInfoAllowedKeys = new Set<string>([
      'additives',
      'servingSize',
      'numberOfServings',
      'addedSugars',
      'biotin',
      'caloriesFromFat',
      'calcium',
      'chloride',
      'cholesterol',
      'choline',
      'chromium',
      'copper',
      'dietaryFiber',
      'folateFolicAcid',
      'iodine',
      'iron',
      'magnesium',
      'manganese',
      'molybdenum',
      'niacin',
      'pantothenicAcid',
      'phosphorus',
      'potassium',
      'protein',
      'riboflavin',
      'saturatedFat',
      'selenium',
      'sugarAlcohol',
      'sugars',
      'thiamin',
      'totalCarbohydrate',
      'totalFat',
      'transFat',
      'vitaminA',
      'vitaminB6',
      'vitaminB12',
      'vitaminC',
      'vitaminD',
      'vitaminE',
      'vitaminK',
      'zinc',
    ]);
    readonly nutritionUnitsAllowed = new Set<string>(['kcal', 'kJ', 'g', 'mg', 'mcg']);
    readonly nutritionStaticKeys = ['additives', 'servingSize', 'numberOfServings'] as const;
    removedNutritionKeysByItemVersion: Record<string, string[]> = {};

    /**
     * Populates the decorators for the given catalogEntryId
     *
     * @param catalogEntryId - catalogEntryId to fetch decorators for
     */
    populateDecorators(catalogEntryId: string): void {
      // cancel last subscription so we don't get incorrect data
      this.decoratorSubscription.unsubscribe();
      if (!catalogEntryId) {
        this.decorators = [];
        this.decoratorsCatalogEntryId = undefined;
        this.pendingDecoratorsCatalogEntryId = undefined;
        this.loadingDecorators = false;
        this.hasDecoratorLoadError = false;
        if (this.form?.get('decorators')) {
          this.form.get('decorators').setValue([]);
        }
        if (this.form?.get('images')) {
          this.form.get('images').setValue({});
        }
        return;
      }
      this.loadingDecorators = true;
      this.pendingDecoratorsCatalogEntryId = catalogEntryId;
      this.decoratorsCatalogEntryId = undefined;
      if (this.brandId) {
        this.decoratorSubscription = this.decoratorService
          .get(this.brandId, catalogEntryId)
          .subscribe(
            (response: Decorator[]) => {
              if (this.pendingDecoratorsCatalogEntryId !== catalogEntryId) {
                return;
              }

              this.decorators = response;
              this.decoratorsCatalogEntryId = catalogEntryId;
              this.applyDecoratorsForCurrentItem();
            },
            () => {
              if (this.pendingDecoratorsCatalogEntryId !== catalogEntryId) {
                return;
              }

              this.errorMessage = this.translateService.instant(
                'catalog.common.errorLoadTranslationsMsg',
              );
              this.hasDecoratorLoadError = true;
              this.loadingDecorators = false;
            },
          );
      } else {
        this.brandService.currentBrandBehaviorSubject
          .pipe(
            takeUntil(this.unsubscribe),
            first((brand: any) => brand.isSet),
          )
          .subscribe(
            (brand) => {
              this.brandId = brand.id;
              this.populateDecorators(catalogEntryId);
            },
            (e) => {
              // ignore error that happens if unsubscribe occurs before brand is set
              // error handler so unit tests don't throw an error in afterAll
            },
          );
      }
    }

    /**
     * A merged images blob stores every channel, and a trash clear is "".
     * Keep "" only when that key has a published image. An empty channel that
     * was never set must not become a draft change marker.
     */
    pruneImageClearsWithoutPublishedImage(): void {
      if (
        !this.decoratorsCatalogEntryId ||
        this.decoratorsCatalogEntryId !== this.itemChangesCatalogEntryId
      ) {
        return;
      }

      const publishedImageKeys = new Set<string>();
      (this.decorators || []).forEach((decorator) => {
        if (decorator.field === 'images' && decorator.value) {
          publishedImageKeys.add(decorator.key);
        }
      });
      const entityImages = this.currentItemForChangeSet?.images || {};
      Object.keys(entityImages).forEach((key) => {
        if (entityImages[key]) {
          publishedImageKeys.add(key);
        }
      });

      [ChangeSetItemType.DRAFT, ChangeSetItemType.SCHEDULED].forEach((version) => {
        const changesForVersion = this.itemChanges?.[version];
        const decoratorChanges = changesForVersion?.decorators;
        if (!decoratorChanges?.length) {
          return;
        }

        changesForVersion.decorators = decoratorChanges.filter((change) => {
          if (change.field !== 'images' || change.value) {
            return true;
          }
          return publishedImageKeys.has(change.key);
        });
      });
    }

    /**
     * Writes published (and matching changeset) decorators to the form once both
     * the catalog GET and this item's changeset load have finished.
     */
    applyDecoratorsForCurrentItem(): void {
      if (
        !this.pendingDecoratorsCatalogEntryId ||
        this.decoratorsCatalogEntryId !== this.pendingDecoratorsCatalogEntryId
      ) {
        return;
      }

      const needsChanges =
        this.selectedVersion === ChangeSetItemType.DRAFT ||
        this.selectedVersion === ChangeSetItemType.SCHEDULED;

      if (needsChanges && this.itemChangesCatalogEntryId !== this.decoratorsCatalogEntryId) {
        return;
      }

      this.pruneImageClearsWithoutPublishedImage();

      const decoratorChanges =
        this.itemChangesCatalogEntryId === this.decoratorsCatalogEntryId
          ? this.itemChanges?.[this.selectedVersion]?.decorators
          : undefined;
      const decoratorsForForm = this.mergeDecoratorsWithChanges(this.decorators, decoratorChanges);

      this.form.get('decorators').setValue(decoratorsForForm);
      this.ensurePublishedImageBaseline();
      this.imageFormValues = {};
      this.imageDecorators = [];
      for (const item of decoratorsForForm) {
        if (item.field === 'images') {
          this.imageDecorators.push(item);
          this.imageFormValues[item.key] = item.value;
        }
      }
      this.form.get('images')?.setValue(this.imageFormValues);
      this.form.markAsPristine();
      this.hasDecoratorLoadError = false;
      this.loadingDecorators = false;
    }

    /**
     * Call the save decorators function and return the observable result
     * @param selectedItem - item to save decorators for
     * @param decoratorsProvided - array of decorators to save
     * @return Observable<Decorator[]>
     */
    saveDecorators = (selectedItem, decoratorsProvided: Decorator[]): Observable<Decorator[]> => {
      return decoratorsProvided
        ? this.decoratorService
            .save(
              this.brandId,
              selectedItem.catalogEntryId,
              decoratorsProvided,
              DecoratorType.CATALOG,
            )
            .pipe(
              catchError(() => {
                this.errorMessage += this.translateService.instant(
                  'catalog.common.saveTranslationsFailMsg',
                );
                return of(undefined);
              }),
            )
        : of([]);
    };

    async populateAvailableTags() {
      this.isLoadingTags = true;
      this.availableTags = [];
      this.tagLookup = {};
      const observable = this.tagsService.getAll().pipe(
        takeUntil(this.unsubscribe),
        catchError((err) => {
          this.showLoadTagsError = true;
          return of(undefined);
        }),
      );

      observable.subscribe({
        next: (tags) => {
          if (tags) {
            const formattedTags = tags.map((tag) => {
              if (tag.name.includes('_')) {
                // Replace underscore with space
                tag.name = tag.name.replace(/_/g, ' ');
              }
              return tag;
            });

            this.availableTags = this.availableTags.concat(formattedTags);
            this.marketingTagsFullList = this.availableTags.filter(
              (item) =>
                item.type !== TagType.Allergy.toString() ||
                item.type !== TagType.Dietary.toString(),
            );

            Object.assign(this.tagLookup, this.utilitiesService.convertArrayToMap(tags, 'id'));
            this.form.markAsPristine();
            this.isLoadingTags = false;
          }
        },
        error: () => {
          this.pageErrorMessage = this.translateService.instant('catalog.tags.loadFailMsg');
          this.isLoadingTags = false;
        },
      });

      return observable.toPromise();
    }

    populateTagAssociations(catalogEntryId: string): void {
      if (!catalogEntryId) {
        return;
      }

      this.tagAssociationList = [];
      this.isLoadingAssociations = true;
      this.isLoadingTagAssociations = true;
      this.tagsService.getAssociationsByItemId(catalogEntryId).subscribe({
        next: (data) => {
          this.tagAssociationList = this.tagAssociationList.concat(data);
          this.isLoadingAssociations = false;
          this.isLoadingTagAssociations = false;
        },
        error: () => {
          this.showLoadTagsError = true;
          this.isLoadingAssociations = false;
          this.isLoadingTagAssociations = false;
        },
        complete: () => {
          const allergyTagIds = this.tagAssociationList
            .filter((item) => item.type === TagType.Allergy.toString())
            .map((i) => {
              return { tagId: i.id, levelOfContainment: i.levelOfContainment };
            });
          const dietaryTagIds = this.tagAssociationList
            .filter((item) => item.type === TagType.Dietary.toString())
            .map((i) => {
              return { tagId: i.id };
            });

          this.form.get('allergyTagIds')?.setValue(allergyTagIds);
          this.form.get('dietaryTagIds')?.setValue(dietaryTagIds);

          const selectedTagIds = this.tagAssociationList.filter(
            (tag) => tag.type !== TagType.Allergy && tag.type !== TagType.Dietary,
          );

          this.form.get('selectedTagIds')?.setValue(selectedTagIds);

          this.tagAssociationList.forEach((tag) => {
            this.tagsService.getAssociationsByTagId(tag.id).subscribe((catalogIdList) => {
              if (this.tagLookup[tag.id]) {
                this.tagLookup[tag.id].associatedEntities = catalogIdList;
              }
            });
          });

          this.tagAssociationsToBackup = {
            selectedTagIds,
            allergyTagIds,
            dietaryTagIds,
          };

          this.form.markAsPristine();
        },
      });
    }

    filterTagsByType(): { marketingTagIds: Tag[]; ueTaxCategoryIds: Tag[] } {
      const marketingTagIds = this.tagAssociationList.filter(
        (tag) => tag.type === TagType.Marketing.toString(),
      );

      const ueTaxCategoryIds = this.tagAssociationList.filter(
        (tag) => tag.type === TagType.UeTaxCategory.toString(),
      );
      return { marketingTagIds, ueTaxCategoryIds: ueTaxCategoryIds };
    }

    splitSelectedTagIdsForChangeSet(selectedTagIds: Tag[] = []): {
      marketingTagIds: Tag[];
      ueTaxCategoryIds: Tag[];
    } {
      const tags = Array.isArray(selectedTagIds) ? selectedTagIds : [];
      const marketingTypes = new Set<string>([
        TagType.Marketing,
        TagType.Promotional,
        TagType.Legal,
      ]);

      const marketingTagIds: Tag[] = [];
      const ueTaxCategoryIds: Tag[] = [];

      tags.forEach((tag) => {
        if (marketingTypes.has(tag.type)) {
          marketingTagIds.push(tag);
        } else if (tag.type === TagType.UeTaxCategory) {
          ueTaxCategoryIds.push(tag);
        }
      });

      return { marketingTagIds, ueTaxCategoryIds };
    }

    getPublishedTagBaselineForChangeSet(): { marketingTagIds: Tag[]; ueTaxCategoryIds: Tag[] } {
      const selectedTagIds = this.tagAssociationsToBackup?.selectedTagIds || [];
      return this.splitSelectedTagIdsForChangeSet(selectedTagIds);
    }

    posItemLookup() {
      this.menuDataService.posItemsLookup
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (
            status: MenuDataStatus<{ [dataGroupId: string]: { [rawPosValue: string]: PosItem } }>,
          ) => {
            this.rawPosLookup = status.data;
          },
        );
    }

    prepareForCancel(selectedItem: any) {
      this.changeSetsService.isResetting = true;

      if (this.selectedVersion !== ChangeSetItemType.PUBLISHED) {
        if (this.cachedVersion) {
          this.itemChanges[this.selectedVersion] = this.cachedVersion;
          delete this.cachedVersion;
        }

        // restoring changes
        this.setCurrentVersion({ version: this.selectedVersion, selectedItem });
        this.changeSetsService.isResetting = false;
        return;
      }

      this.handleCancel();
      this.changeSetsService.isResetting = false;
    }

    prepareForSave(formData) {
      formData = formData || this.form.getRawValue();

      const selectedItem =
        (this as any).selectedProduct ||
        (this as any).selectedModifier ||
        (this as any).selectedCombo ||
        (this as any).selectedItem;
      const nutritionInfoControl = this.form.get('nutritionInfo');
      let nutritionInfo =
        formData.nutritionInfo && Object.keys(formData.nutritionInfo).length > 0
          ? formData.nutritionInfo
          : nutritionInfoControl?.value && Object.keys(nutritionInfoControl.value).length > 0
            ? nutritionInfoControl.value
            : (selectedItem as any)?.nutritionInfo;
      const nutritionTexts =
        formData.nutritionTexts && Object.keys(formData.nutritionTexts).length > 0
          ? formData.nutritionTexts
          : this.form.get('nutritionTexts')?.value &&
              Object.keys(this.form.get('nutritionTexts').value).length > 0
            ? this.form.get('nutritionTexts').value
            : (selectedItem as any)?.nutritionTexts || {};

      if (nutritionInfo) {
        nutritionInfo = DeepCloneService.clone(nutritionInfo);
        const nutritionalFields = ['additives', 'allergenInfo', 'ingredientsInfo', 'nutritionInfo'];
        const decoratorControl = this.form.get('decorators');
        let curators =
          formData.decorators || decoratorControl?.value || (selectedItem as any)?.decorators || [];

        nutritionalFields.forEach((field) => {
          const value = field === 'additives' ? nutritionInfo.additives : nutritionTexts[field];
          if (!value) {
            curators = curators.filter((decorator) => decorator.field !== field);
          }
        });

        if (decoratorControl) {
          decoratorControl.setValue(curators, { emitEvent: false });
        }
        if (formData.decorators || (selectedItem as any)?.decorators || curators?.length) {
          formData.decorators = curators;
        }

        Object.keys(nutritionInfo).forEach((key) => {
          if (typeof nutritionInfo[key] === 'object' && nutritionInfo[key] !== null) {
            if (!nutritionInfo[key].unit) {
              delete nutritionInfo[key];
            } else {
              delete nutritionInfo[key].amount;
            }
          }
        });

        formData.nutritionInfo = nutritionInfo;
      }

      if (nutritionTexts && Object.keys(nutritionTexts).length > 0) {
        formData.nutritionTexts = DeepCloneService.clone(nutritionTexts);
      }

      if (typeof this.handleSave === 'function') {
        let decorators = this.form.get('decorators').value;
        let activeTabIndex = this.tabGroup.selectedIndex;
        if (!decorators?.length) {
          this.tabGroup.selectedIndex = 0;
          this.isLoading = true;
          setTimeout(() => {
            this.tabGroup.selectedIndex = activeTabIndex;
            this.handleSave(formData);
            this.isLoading = false;
          }, 5000);
        } else {
          this.handleSave(formData);
        }
      }
    }

    // this function is used to display mat errors into a tool tip and a red cross icon. This function is generalized to use in any form where min and max field validators are required.
    getErrorsTooltipVal(errors) {
      let msg = [];
      for (let key of Object.keys(errors)) {
        switch (key) {
          case 'min':
            msg.push(this.translateService.instant('catalog.common.numLessThanZeroError'));
            break;
          case 'max':
            msg.push(this.translateService.instant('catalog.product.greaterThanModifierGroupMax'));
            break;
          case 'lessThan':
            msg.push(this.translateService.instant('catalog.common.minGreaterThanMax'));
            break;
          case 'greaterThan':
            msg.push(this.translateService.instant('catalog.common.maxLessThanMin'));
            break;
          case 'inBetween':
            msg.push(this.translateService.instant('catalog.common.inBetweenError'));
            break;
          case 'pattern':
            msg.push(this.translateService.instant('catalog.common.alphanumericOnly'));
            break;
        }
      }
      return msg.join(', ');
    }

    //Allow to enter decimals with or without a dash for range input. Used for calories range input
    isNumberKey(event) {
      const charCode = event?.which ? event?.which : event?.keyCode;
      const input = event.target.value;
      const cursorPos = event.target.selectionStart;

      // Allow digits (0-9)
      if (charCode >= 48 && charCode <= 57) return true;

      // Allow dash (only one, not at start)
      if (charCode === 45) {
        return !input.includes('-') && cursorPos > 0;
      }

      // Allow decimal point (one per segment)
      if (charCode === 46) {
        const dashIndex = input.indexOf('-');
        const beforeDash = input.substring(0, cursorPos);
        const afterDash = dashIndex >= 0 ? input.substring(dashIndex + 1, cursorPos) : '';

        // Check if current segment already has a decimal
        if (dashIndex === -1 || cursorPos <= dashIndex) {
          // Before dash or no dash - check beforeDash for decimal
          return !beforeDash.includes('.');
        } else {
          // After dash - check afterDash for decimal
          return !afterDash.includes('.');
        }
      }

      return false;
    }

    // common function to handle calories count in products, combos and modifiers
    onCaloriesCountChange(caloricCount?) {
      if (caloricCount && Object.keys(caloricCount)?.length) {
        let calories =
          (caloricCount.lowerRangeInKcal === 0 || caloricCount.lowerRangeInKcal) &&
          caloricCount.higherRangeInKcal
            ? caloricCount.lowerRangeInKcal + '-' + caloricCount.higherRangeInKcal
            : caloricCount.lowerRangeInKcal;
        this.form.get('calories')?.setValue(calories);
      }

      const calories = this.form.get('calories')?.value.toString();
      if (calories?.length && calories.includes('-')) {
        let [lowerRange, higherRange] = calories.split('-');
        const lowerNum = parseFloat(lowerRange);
        const higherNum = parseFloat(higherRange);
        if (isNaN(lowerNum) || isNaN(higherNum) || higherNum - lowerNum < 0) {
          this.form.get('calories')?.setErrors({ invalidRange: true });
        } else {
          this.form.get('caloricCountInput').patchValue({
            caloricUnit: this.brand.defaultCaloricUnit || 'kcal',
            lowerRange: lowerNum,
            higherRange: higherNum,
            servings: 1,
          });
        }
      } else if (calories?.length && !calories.includes('-')) {
        this.form.get('caloricCountInput').patchValue({
          caloricUnit: this.brand.defaultCaloricUnit || 'kcal',
          lowerRange: parseFloat(calories),
          higherRange: null,
          servings: 1,
        });
      } else {
        this.form.get('caloricCountInput')?.setValue({
          caloricUnit: null,
          lowerRange: null,
          higherRange: null,
          servings: null,
        });
      }
    }

    onSelectSodiumMeasure(measure) {
      const unitControl = this.form.get('sodium').get('unit');

      unitControl.setValue(measure);
      unitControl.markAsDirty();
    }

    onABVChange() {
      return this.form
        ?.get('alcohol')
        ?.get('enabled')
        ?.setValue(!!Number(this.form?.get('alcohol')?.get('volume')?.value));
    }

    hasSodiumAmountValue(sodium: any): boolean {
      if (!sodium) {
        return false;
      }

      return ['value', 'lowerRange', 'higherRange', 'unit'].some((field) => {
        const sodiumFieldValue = sodium[field];
        return (
          sodiumFieldValue !== null && sodiumFieldValue !== undefined && sodiumFieldValue !== ''
        );
      });
    }

    isSodiumRowActive(): boolean {
      return !!this.nutritionInfo?.activeNutritionKeys?.includes('sodium');
    }

    shouldIncludeSodiumInSave(sodium: any): boolean {
      if (!this.hasSodiumAmountValue(sodium) || !sodium?.unit) {
        return false;
      }

      if (this.nutritionInfo?.activeNutritionKeys) {
        return this.isSodiumRowActive();
      }

      return true;
    }

    applySodiumSaveRules(saveData: Record<string, any>, selectedItem: any): void {
      if (!saveData) {
        return;
      }

      if (!this.shouldIncludeSodiumInSave(saveData.sodium)) {
        if (selectedItem?.sodium) {
          saveData.sodium = null;
        } else {
          delete saveData.sodium;
        }
      } else if (saveData.sodium) {
        delete saveData.sodium.amount;
        delete saveData.sodium.value;
      }
    }

    // The catalog-entry PATCH ignores caloricCountInput: null and rejects {}, so clearing
    // stored calories has to go through the remove list.
    getCaloricCountRemoveKeys(saveData: Record<string, any>, selectedItem: any): string[] {
      // caloricCount comes from the GET. A value saved in this session is only on
      // caloricCountInput, because the post-save assign copies the form payload.
      const hadStoredCalories = selectedItem?.caloricCount || selectedItem?.caloricCountInput;
      return !saveData?.calories && hadStoredCalories ? [CALORIC_COUNT_REMOVE_KEY] : [];
    }

    clearRemovedCaloricCount(selectedItem: any, removeKeys: string[]): void {
      if (selectedItem && removeKeys?.includes(CALORIC_COUNT_REMOVE_KEY)) {
        delete selectedItem.caloricCount;
        delete selectedItem.caloricCountInput;
      }
    }

    isDecoratorChangedFromPublished(decorator: Decorator): boolean {
      const publishedDecorators: Decorator[] = this.cachedBeforeVersionChange?.decorators?.length
        ? this.cachedBeforeVersionChange.decorators
        : this.decorators;

      if (!publishedDecorators?.length) {
        return !!decorator.value;
      }

      const published = publishedDecorators.find(
        (item) => item.key === decorator.key && item.field === decorator.field,
      );

      if (!published) {
        return !!decorator.value;
      }

      return published.value !== decorator.value;
    }

    isDecoratorUnchangedInDraftBaseline(decorator: Decorator): boolean {
      const draftDecorators = this.draftDecoratorsBaselineForSave;

      if (!draftDecorators?.length) {
        return false;
      }

      const existing = draftDecorators.find(
        (item) => item.key === decorator.key && item.field === decorator.field,
      );

      if (!existing) {
        return false;
      }

      return existing.value === decorator.value;
    }

    shouldIncludeDecoratorForUpsert(decorator: Decorator, controlToCheck: any): boolean {
      if (!controlToCheck) {
        return true;
      }

      if (controlToCheck.dirty) {
        return true;
      }

      if (!this.isDecoratorChangedFromPublished(decorator)) {
        return false;
      }

      return !this.isDecoratorUnchangedInDraftBaseline(decorator);
    }

    shouldIncludeImageForUpsert(imageKey: string, currentValue: any, imageControl: any): boolean {
      if (imageControl?.dirty) {
        return true;
      }

      if (!this.isImageChangedFromPublished(imageKey, currentValue)) {
        return false;
      }

      return !this.isDecoratorUnchangedInDraftBaseline({
        key: imageKey,
        field: 'images',
        value: currentValue,
      });
    }

    getChangeSetImageControl(imageKey: string) {
      if (typeof this.itemImages?.getChangeSetImageControl === 'function') {
        return this.itemImages.getChangeSetImageControl(imageKey);
      }

      return (
        this.itemImages?.form?.get?.(imageKey) ??
        this.itemImages?.form?.controls?.[imageKey] ??
        null
      );
    }

    /**
     * Keys eligible for image changeset upserts.
     * Merges component-reported keys with localized body.images keys that still
     * have a token control (covers deletes that no longer appear in localizedControls values).
     */
    getChangeSetImageKeysForKeeping(bodyImages?: Record<string, unknown>): string[] {
      const keys = new Set<string>(
        this.itemImages?.getChangeSetImageKeys?.() ??
          Object.keys(this.itemImages?.form?.controls || {}),
      );

      Object.keys(bodyImages || {}).forEach((imageKey) => {
        if (isLocalizedImageKey(imageKey) && this.getChangeSetImageControl(imageKey)) {
          keys.add(imageKey);
        }
      });

      return Array.from(keys);
    }

    /**
     * Draft channel images live in one merged upsert.base.images object.
     * Reverting a channel must drop that key; an empty string keeps it in the
     * changeset. Only keys that still differ from published are re-upserted.
     * Live save images win over the draft cache so a same-save edit is not
     * overwritten by a stale cached URL.
     */
    getRemainingDraftImages(
      deletedImageKeys: string[],
      bodyImages?: Record<string, unknown>,
    ): Record<string, unknown> {
      const remaining: Record<string, unknown> = {};
      const sources = [bodyImages, this.cachedAfterVersionChange?.form?.images];

      sources.forEach((images) => {
        Object.keys(images || {}).forEach((key) => {
          if (deletedImageKeys.includes(key) || remaining[key] != null) {
            return;
          }
          const value = images[key];
          if (value == null || value === '') {
            return;
          }
          if (!this.isImageChangedFromPublished(key, value)) {
            return;
          }
          remaining[key] = value;
        });
      });

      return remaining;
    }

    isImageChangedFromPublished(imageKey: string, currentValue: any): boolean {
      const baseline = this.cachedBeforeVersionChange;

      if (!baseline) {
        return false;
      }

      const oldImages = baseline.form?.images;

      if (oldImages?.hasOwnProperty(imageKey)) {
        return oldImages[imageKey] !== currentValue;
      }

      const publishedDecorator = (baseline.decorators || []).find(
        (decorator) => decorator.field === 'images' && decorator.key === imageKey,
      );

      if (!publishedDecorator) {
        return !!currentValue;
      }

      return publishedDecorator.value !== currentValue;
    }

    /**
     * Published image URLs used when reverting a draft/scheduled image change.
     * Rebuilt from loaded published decorators when the item changed while already
     * on draft (cachedBeforeVersionChange is cleared on item switch).
     */
    ensurePublishedImageBaseline(): void {
      if (!this.cachedBeforeVersionChange) {
        this.cachedBeforeVersionChange = {};
      }

      if (!this.cachedBeforeVersionChange.form) {
        this.cachedBeforeVersionChange.form = {};
      }

      if (!this.cachedBeforeVersionChange.decorators) {
        this.cachedBeforeVersionChange.decorators = DeepCloneService.clone(this.decorators || []);
      }

      if (this.cachedBeforeVersionChange.form.images) {
        return;
      }

      const publishedImages: Record<string, unknown> = {
        ...(this.currentItemForChangeSet?.images || {}),
      };

      for (const item of this.decorators || []) {
        if (item.field === 'images' && item.value) {
          publishedImages[item.key] = item.value;
        }
      }

      this.cachedBeforeVersionChange.form.images = publishedImages;
    }

    getPublishedImageBaselineValue(imageKey: string) {
      this.ensurePublishedImageBaseline();

      const cachedImages = this.cachedBeforeVersionChange?.form?.images;
      if (cachedImages && Object.prototype.hasOwnProperty.call(cachedImages, imageKey)) {
        return cachedImages[imageKey];
      }

      const fromCacheDecorator = (this.cachedBeforeVersionChange?.decorators || []).find(
        (decorator) => decorator.field === 'images' && decorator.key === imageKey,
      );
      if (fromCacheDecorator?.value != null) {
        return fromCacheDecorator.value;
      }

      const fromPublishedDecorator = (this.decorators || []).find(
        (decorator) => decorator.field === 'images' && decorator.key === imageKey,
      );
      return fromPublishedDecorator?.value;
    }

    createPatchBodyForUpsert(
      body: any,
      deletedKeys: string[],
      entityType: CatalogLookupType,
      entityId: string,
    ): Observable<PatchCatalogChangesInput> {
      body = trimCatalogItemFields(body);
      const createRegularIdentifier = (field) => (key) => field === key;
      const identifiersForUpsert = {
        base: (key) =>
          ['name', 'title', 'description', 'displayType', 'externalId', 'alternateId'].includes(
            key,
          ),
        tag: (key) =>
          ['allergyTagIds', 'dietaryTagIds', 'marketingTagIds', 'ueTaxCategoryIds'].includes(key),
        alcohol: createRegularIdentifier('alcohol'),
        nutritionInfo: createRegularIdentifier('nutritionInfo'),
        images: createRegularIdentifier('images'),
        sodium: createRegularIdentifier('sodium'),
        calories: (key) => ['calories', 'caloricCountInput'].includes(key),
        upsell: createRegularIdentifier('upsellData'),
        decorator: createRegularIdentifier('decorators'),
        composite: (key) =>
          entityType === CatalogLookupType.composite &&
          ['compositeType', 'productGroupContextData', 'productGroupIds'].includes(key),
        modifierGroup: (key) =>
          entityType === CatalogLookupType.modifierGroup &&
          [
            'modifiers',
            'modifierIds',
            'minimumQuantity',
            'maximumQuantity',
            'modifierQuantities',
            'hiddenModifiers',
            'sku',
          ].includes(key),
        productGroup: (key) =>
          entityType === CatalogLookupType.productGroup &&
          [
            'productIds',
            'minimumQuantity',
            'maximumQuantity',
            'defaultProductIds',
            'isHeroProductGroup',
          ].includes(key),
        product: (key) =>
          entityType === CatalogLookupType.product &&
          ['modifierGroupIds', 'modifierGroupContextData'].includes(key),
      };
      const patchBody: PatchCatalogChangesInput = {
        catalogType: entityType,
        upsert: {},
        delete: {},
      };

      const backendKeyMap = {
        tags: 'tag',
      };
      const identifiersForDelete = {
        base: (key) =>
          [
            'title',
            'description',
            'name',
            'tags',
            'alcohol',
            'sodium',
            'calories',
            'upsell',
            'displayType',
            'externalId',
            'alternateId',
          ].includes(key),
        fullEntity: (key) =>
          ['modifierGroup', 'productGroup', 'composite', 'product'].includes(key),
      };
      const deleteDecoratorMap: { [key: string]: any } = {};

      deletedKeys.forEach((key) => {
        const changeKey = Object.keys(identifiersForDelete).find((identifierKey) => {
          const identifierFunction = identifiersForDelete[identifierKey];

          return identifierFunction(key);
        });

        switch (changeKey) {
          case 'base':
            if (!patchBody.delete.base) {
              patchBody.delete.base = [];
            }

            patchBody.delete.base.push(backendKeyMap[key] || key);
            break;
          case 'fullEntity':
            if (!patchBody.delete.fullEntity) {
              patchBody.delete.fullEntity = [];
            }

            patchBody.delete.fullEntity.push(backendKeyMap[key] || key);
            break;
          default:
            if (key.includes('decorator')) {
              // key format is: decorator_<key>_<field>
              const nameSplit = key.split('_');

              if (!deleteDecoratorMap[nameSplit[2]]) {
                deleteDecoratorMap[nameSplit[2]] = [];
              }

              deleteDecoratorMap[nameSplit[2]].push(nameSplit[1]);
            }
            break;
        }
      });

      const allowedDecorators = [
        'description',
        'images',
        'title',
        'subTitle',
        'upSellContext.description',
      ];
      const upsertDecoratorMap: { [key: string]: any } = {};

      // populating decorators
      (body.decorators || []).forEach((decorator) => {
        if (!allowedDecorators.includes(decorator.field)) {
          return;
        }

        const isNotDeleted = !deleteDecoratorMap[decorator.field]?.includes(decorator.key);

        if (isNotDeleted) {
          let controlToCheck;

          if (decorator.field === 'images') {
            controlToCheck = this.getChangeSetImageControl(decorator.key);
          }

          if (decorator.field === 'upSellContext.description') {
            controlToCheck = this.form?.controls?.upsellData;
          }

          if (
            this.itemLocalization?.itemDecorator &&
            ['title', 'description', 'subTitle'].includes(decorator.field)
          ) {
            const brandHasLanguage =
              this.itemLocalization.itemDecorator.form.controls?.[decorator.key];

            if (brandHasLanguage) {
              controlToCheck = brandHasLanguage.controls?.[decorator.field];
            } else {
              // does not need to save, control is not there, probably old decorator after brand was edited
              return;
            }
          }

          if (this.shouldIncludeDecoratorForUpsert(decorator, controlToCheck)) {
            if (!upsertDecoratorMap[decorator.field]) {
              upsertDecoratorMap[decorator.field] = {};
            }

            // For image decorators with string values (e.g., baseImage applied to channels),
            // omit entityId since string values don't trigger upload-url generation
            const shouldOmitEntityId =
              decorator.field === 'images' && typeof decorator.value === 'string';
            upsertDecoratorMap[decorator.field][decorator.key] = {
              value: decorator.value,
              entityId: shouldOmitEntityId ? undefined : decorator.entityId,
            };
          }
        }
      });

      Object.keys(OmitService.omit(body, 'decorators')).forEach((key) => {
        const changeKey = Object.keys(identifiersForUpsert).find((identifierKey) => {
          const identifierFunction = identifiersForUpsert[identifierKey];

          return identifierFunction(key);
        });

        switch (changeKey) {
          case 'base':
            if (body[key] !== undefined) {
              patchBody.upsert.base = {
                ...patchBody.upsert.base,
                [key]: body[key],
              };
            }
            break;
          case 'images':
            if (body[key]) {
              if (!patchBody.upsert.base) {
                patchBody.upsert.base = {};
              }

              if (!patchBody.upsert?.base.images) {
                patchBody.upsert.base.images = {};
              }

              const oldImagesObject =
                (IsEmptyService.isEmpty(this.cachedAfterVersionChange)
                  ? this.cachedBeforeVersionChange
                  : this.cachedAfterVersionChange
                )?.form?.images || {};
              const keysForKeeping = this.getChangeSetImageKeysForKeeping(body[key]);

              Object.keys(body[key]).forEach((imageKey) => {
                const imageControl = this.getChangeSetImageControl(imageKey);
                const imageChanged = this.shouldIncludeImageForUpsert(
                  imageKey,
                  body[key][imageKey],
                  imageControl,
                );

                if (!keysForKeeping.includes(imageKey) || (imageControl && !imageChanged)) {
                  return;
                }

                const isNotEmpty = !!body[key][imageKey];
                const deletedFromImages = oldImagesObject[imageKey] && !body[key][imageKey];
                const isNotDeleted = !deleteDecoratorMap.images?.includes(imageKey);
                const isFile = body.images[imageKey] instanceof File;

                if (isNotDeleted && (isNotEmpty || deletedFromImages)) {
                  if (!patchBody.upsert.base) {
                    patchBody.upsert.base = {};
                  }

                  if (isFile) {
                    if (!upsertDecoratorMap.images) {
                      upsertDecoratorMap.images = {};
                    }

                    upsertDecoratorMap.images[imageKey] = {
                      value: body[key][imageKey],
                    };
                  } else {
                    patchBody.upsert.base.images[imageKey] = deletedFromImages
                      ? ''
                      : body[key][imageKey];

                    if (deletedFromImages) {
                      if (!upsertDecoratorMap.images) {
                        upsertDecoratorMap.images = {};
                      }
                      upsertDecoratorMap.images[imageKey] = { value: '', entityId };
                    }
                  }
                }
              });

              if (IsEmptyService.isEmpty(patchBody.upsert.base.images)) {
                delete patchBody.upsert.base.images;
              }
            }
            break;
          case 'tag':
            if (Array.isArray(body[key])) {
              const isNotDeleted = !patchBody.delete?.base?.includes('tag');

              if (isNotDeleted) {
                const changesForVersion = this.itemChanges?.[this.selectedVersion];
                const shouldSet =
                  changesForVersion?.[key] ||
                  GetService.get(changesForVersion, `tags.${key}`) ||
                  body.hasOwnProperty(key);

                if (!shouldSet) {
                  break;
                }

                if (!patchBody.upsert.tag) {
                  patchBody.upsert.tag = {};
                }

                if (key === 'marketingTagIds') {
                  patchBody.upsert.tag.marketingTagIds = body[key].map((tag) => tag.id);
                }

                if (key === 'ueTaxCategoryIds') {
                  patchBody.upsert.tag.ueTaxCategoryIds = body[key].map((tag) => tag.id);
                }

                if (key === 'dietaryTagIds') {
                  patchBody.upsert.tag.dietaryTagIds = body[key].map((tag) => tag.tagId);
                }

                if (key === 'allergyTagIds') {
                  patchBody.upsert.tag.allergyTagIds = body[key];
                }
              }
            }
            break;
          case 'calories':
            if (patchBody.delete?.base?.includes('calories')) {
              break;
            }

            if (!body.hasOwnProperty('calories') && !body.hasOwnProperty('caloricCountInput')) {
              break;
            }

            const normalizedCalories = this.normalizeCaloriesForPatch(body);
            if (normalizedCalories) {
              patchBody.upsert.calories = normalizedCalories;
            } else {
              delete patchBody.upsert.calories;
            }
            break;
          case 'alcohol':
            if (patchBody.delete?.base?.includes('alcohol') || !body.hasOwnProperty('alcohol')) {
              break;
            }

            if (!!body.alcohol?.volume) {
              patchBody.upsert.alcohol = body[key];
            } else {
              // deleting alcohol
              patchBody.upsert.alcohol = {
                enabled: false,
                volume: 0,
              };
            }
            break;
          case 'nutritionInfo':
            if (
              patchBody.delete?.base?.includes('nutritionInfo') ||
              !body.hasOwnProperty('nutritionInfo')
            ) {
              break;
            }

            const normalizedNutritionInfo = this.sanitizeNutritionInfoForPatch(body[key]);
            if (!IsEmptyService.isEmpty(normalizedNutritionInfo)) {
              patchBody.upsert.nutritionInfo = normalizedNutritionInfo;
            } else {
              delete patchBody.upsert.nutritionInfo;
            }
            break;
          case 'sodium':
            if (patchBody.delete?.base?.includes('sodium') || !body.hasOwnProperty('sodium')) {
              break;
            }

            const normalizedSodium = this.normalizeSodiumForPatch(body.sodium);
            if (normalizedSodium) {
              (patchBody.upsert as any).sodium = normalizedSodium;
            } else {
              delete patchBody.upsert.sodium;
            }
            break;
          case 'upsell':
            if (patchBody.delete?.base?.includes('upsell')) {
              break;
            }

            if (!IsEmptyService.isEmpty(body[key]) && body[key].description) {
              const { description, catalogEntryIds, decorators } = body[key];

              patchBody.upsert.upsell = {
                catalogEntryIds: Array.isArray(catalogEntryIds) ? catalogEntryIds : [],
                description,
              };

              decorators.forEach((decorator) => {
                if (!allowedDecorators.includes(decorator.field)) {
                  return;
                }

                if (!deleteDecoratorMap[decorator.field]?.includes(decorator.key)) {
                  if (decorator.value) {
                    if (!upsertDecoratorMap[decorator.field]) {
                      upsertDecoratorMap[decorator.field] = {};
                    }

                    upsertDecoratorMap[decorator.field][decorator.key] = {
                      value: decorator.value,
                      entityId: decorator.entityId,
                    };
                  }
                }
              });
            }
            break;
          case 'composite':
          case 'modifierGroup':
          case 'productGroup':
          case 'product':
            const isNotDeleted = !patchBody.delete?.fullEntity?.includes(changeKey);
            const isNotModGroup = !['productGroup', 'modifierGroup'].includes(changeKey);

            if (!Array.isArray(body.modifierIds) && Array.isArray(body.modifiers?.modifierIds)) {
              body.modifierIds = body.modifiers.modifierIds;
            }

            const isProdGroupAndHasIds =
              changeKey === 'productGroup' && Array.isArray(body.productIds);
            const isModGroupAndHasIds =
              changeKey === 'modifierGroup' && Array.isArray(body.modifierIds);
            const isModGroupQuantityChange =
              changeKey === 'modifierGroup' &&
              (Object.prototype.hasOwnProperty.call(body, 'minimumQuantity') ||
                Object.prototype.hasOwnProperty.call(body, 'maximumQuantity'));
            const isModGroupSkuChange =
              changeKey === 'modifierGroup' && Object.prototype.hasOwnProperty.call(body, 'sku');
            const itemGroupCase =
              isNotModGroup ||
              isProdGroupAndHasIds ||
              isModGroupAndHasIds ||
              isModGroupQuantityChange ||
              isModGroupSkuChange;
            const isQuantityKey = key === 'minimumQuantity' || key === 'maximumQuantity';
            const hasQuantityValue =
              isQuantityKey && Object.prototype.hasOwnProperty.call(body, key);
            const isSkuKey = key === 'sku';
            const hasSkuValue = isSkuKey && Object.prototype.hasOwnProperty.call(body, key);

            if ((body[key] || hasQuantityValue || hasSkuValue) && itemGroupCase && isNotDeleted) {
              if (key === 'modifiers') {
                patchBody.upsert[changeKey] = {
                  ...patchBody.upsert[changeKey],
                  ...body[key],
                } as any;
              } else {
                patchBody.upsert[changeKey] = {
                  ...patchBody.upsert[changeKey],
                  [key]: body[key],
                } as any;
              }

              if (['productGroup', 'modifierGroup'].includes(changeKey)) {
                const quantityPatch: Record<string, unknown> = {};

                if (Object.prototype.hasOwnProperty.call(body, 'minimumQuantity')) {
                  quantityPatch.minimumQuantity = body.minimumQuantity;
                }

                if (Object.prototype.hasOwnProperty.call(body, 'maximumQuantity')) {
                  quantityPatch.maximumQuantity = body.maximumQuantity;
                }

                if (
                  changeKey === 'modifierGroup' &&
                  Object.prototype.hasOwnProperty.call(body, 'sku')
                ) {
                  quantityPatch.sku = body.sku;
                }

                Object.assign(patchBody.upsert[changeKey], quantityPatch);
              }

              if (changeKey === 'product') {
                patchBody.upsert.product = {
                  ...patchBody.upsert.product,
                  modifierGroupIds: body.modifierGroupContextData.map((d) => d.modifierGroupId),
                };
              }

              if (IsEmptyService.isEmpty(patchBody.upsert[changeKey])) {
                delete patchBody.upsert[changeKey];
              }
            }
            break;
          default:
            if (!!changeKey && body[key]) {
              patchBody.upsert[key] = body[key];
            }
            break;
        }
      });

      if (!IsEmptyService.isEmpty(deleteDecoratorMap)) {
        if (!patchBody.delete.base) {
          patchBody.delete.base = [];
        }

        const deletesImagesList = [];

        patchBody.delete.decorators = Object.keys(deleteDecoratorMap).reduce((acc, decField) => {
          deleteDecoratorMap[decField].forEach((decKey) => {
            const isImage = decField === 'images';

            if (isImage) {
              deletesImagesList.push(decKey);
            }

            acc.push({
              key: decKey,
              field: decField,
              type: DecoratorType.CATALOG,
              multimedia: decField === 'images',
              value: '',
              entityId,
            });
          });

          return acc;
        }, []);

        if (deletesImagesList.length) {
          const remainingImages = this.getRemainingDraftImages(deletesImagesList, body.images);

          if (IsEmptyService.isEmpty(remainingImages)) {
            if (!patchBody.delete.base) {
              patchBody.delete.base = [];
            }
            if (!patchBody.delete.base.includes('images')) {
              patchBody.delete.base.push('images');
            }
            if (patchBody.upsert?.base?.images) {
              delete patchBody.upsert.base.images;
              if (IsEmptyService.isEmpty(patchBody.upsert.base)) {
                delete patchBody.upsert.base;
              }
            }
          } else {
            if (!patchBody.upsert.base) {
              patchBody.upsert.base = {};
            }
            patchBody.upsert.base.images = remainingImages;
            if (patchBody.delete?.base) {
              patchBody.delete.base = patchBody.delete.base.filter((key) => key !== 'images');
              if (!patchBody.delete.base.length) {
                delete patchBody.delete.base;
              }
            }
          }
        }
      }

      const PNG = 'image/png';
      const JPG = 'image/jpeg';
      const getImageExtensionFromType = (imageType: string): string => {
        switch (imageType) {
          case PNG:
            return 'png';
          case JPG:
            return 'jpg';
          case undefined:
            return '';
          default:
            return imageType.replace('image/', '');
        }
      };

      if (!IsEmptyService.isEmpty(upsertDecoratorMap)) {
        const keysForKeeping = this.getChangeSetImageKeysForKeeping(body.images);
        const upsertDecorators: PatchCatalogChangesInput['upsert']['decorators'] = [];

        Object.keys(upsertDecoratorMap).forEach((decField) => {
          Object.keys(upsertDecoratorMap[decField]).forEach((decKey) => {
            const storedItem = upsertDecoratorMap[decField][decKey];
            if (decField !== 'images') {
              upsertDecorators.push({
                key: decKey,
                field: decField,
                type: DecoratorType.CATALOG,
                value: storedItem.value,
                entityId: storedItem.entityId || entityId,
              });
            } else if (
              keysForKeeping.includes(decKey) &&
              !deleteDecoratorMap.images?.includes(decKey)
            ) {
              const isFile = storedItem.value instanceof File;

              if (!patchBody.upsert.base) {
                patchBody.upsert.base = {};
              }

              if (!patchBody.upsert.base.images) {
                patchBody.upsert.base.images = {};
              }

              if (isFile) {
                upsertDecorators.push({
                  key: decKey,
                  field: decField,
                  type: DecoratorType.CATALOG,
                  multimedia: true,
                  extension: getImageExtensionFromType(storedItem.value.type),
                });
              } else {
                patchBody.upsert.base.images[decKey] = storedItem.value;

                if (!storedItem.value) {
                  upsertDecorators.push({
                    key: decKey,
                    field: decField,
                    type: DecoratorType.CATALOG,
                    value: storedItem.value,
                    multimedia: true,
                    entityId: storedItem.entityId || entityId,
                  });
                }
              }

              if (IsEmptyService.isEmpty(patchBody.upsert.base.images)) {
                delete patchBody.upsert.base.images;
              }
            }
          });
        });

        if (upsertDecorators.length) {
          patchBody.upsert.decorators = upsertDecorators;
        }
      }

      if (IsEmptyService.isEmpty(patchBody.upsert)) {
        delete patchBody.upsert;
      }

      if (IsEmptyService.isEmpty(patchBody.delete)) {
        delete patchBody.delete;
      }

      if (patchBody.delete?.base) {
        patchBody.delete.base = UniqService.uniq(patchBody.delete.base);
      }

      if (patchBody?.delete?.fullEntity) {
        patchBody.delete.fullEntity = UniqService.uniq(patchBody.delete.fullEntity);
      }

      this.draftDecoratorsBaselineForSave = null;

      return of(patchBody);
    }

    getNutritionInfoChangesForChangeSet(): Record<string, any> {
      return this.getDirtyNutritionInfoChanges();
    }

    isNutritionControlDirty(control: any): boolean {
      if (!control) {
        return false;
      }

      if (control.dirty) {
        return true;
      }

      const controls = control.controls;
      if (!controls) {
        return false;
      }

      return Object.keys(controls).some((key) => this.isNutritionControlDirty(controls[key]));
    }

    getDirtyNutritionInfoChanges(excludedKey?: string): Record<string, any> {
      const nutritionInfoControl = this.form.get('nutritionInfo');

      return Object.keys(nutritionInfoControl?.['controls'] || {}).reduce(
        (nutritionInfoObj, nutritionKey) => {
          if (nutritionKey === excludedKey) {
            return nutritionInfoObj;
          }

          const nutritionControl = this.form.get(`nutritionInfo.${nutritionKey}`);
          if (this.isNutritionControlDirty(nutritionControl)) {
            nutritionInfoObj[nutritionKey] = nutritionControl.value;
          }

          return nutritionInfoObj;
        },
        {},
      );
    }

    //change-sets
    saveChangeSetWithItem(
      entityType,
      versionData,
      publishedItem,
      decorators,
      upsellData = null,
    ): Observable<any> {
      versionData = trimCatalogItemFields(versionData);
      if (decorators) {
        decorators = trimCatalogItemFields(decorators);
      }
      const deletedKeys: string[] = [];
      const clonedItem = DeepCloneService.clone(publishedItem);
      const isUncoveredPropertiesVersion =
        this.selectedVersion === ChangeSetItemType.DRAFT ||
        this.selectedVersion === ChangeSetItemType.SCHEDULED;

      versionData.decorators =
        decorators ??
        (isUncoveredPropertiesVersion ? this.form.get('decorators')?.value : undefined);

      this.applySodiumSaveRules(versionData, clonedItem);

      if (!versionData.sodium) {
        // if sodium is not sent in versionData, there was no change
        delete clonedItem.sodium;
      }

      if (entityType === CatalogLookupType.modifierGroup) {
        versionData.modifiers = this.form.get('modifiers').value;
      }

      Object.keys(this.form.controls).forEach((key) => {
        if (
          isUncoveredPropertiesVersion &&
          (UNCOVERED_CHANGE_SET_FIELDS as readonly string[]).includes(key)
        ) {
          return;
        }

        if (this.form.controls[key].dirty) {
          if (key === 'sodium' && !this.isSodiumRowActive()) {
            return;
          }

          if (!versionData[key] && this.form.controls[key].value) {
            versionData[key] = this.form.controls[key].value;
          }
        }
      });

      this.applySodiumSaveRules(versionData, clonedItem);

      if (isUncoveredPropertiesVersion) {
        UNCOVERED_CHANGE_SET_FIELDS.forEach((field) => {
          delete versionData[field];
        });
      }

      if (!this.itemChanges?.[this.selectedVersion]) {
        this.itemChanges[this.selectedVersion] = {};
      }

      const changesForVersion = this.itemChanges?.[this.selectedVersion] || {};
      const existingDraftDecorators = changesForVersion?.decorators;

      this.draftDecoratorsBaselineForSave = Array.isArray(existingDraftDecorators)
        ? DeepCloneService.clone(existingDraftDecorators)
        : null;

      const dirtyFieldsObject = Object.keys(this.form.controls).reduce(
        (obj, key) => {
          const control = this.form.controls[key];
          if (!control.dirty) {
            return obj;
          }

          if (
            isUncoveredPropertiesVersion &&
            ((UNCOVERED_CHANGE_SET_FIELDS as readonly string[]).includes(key) ||
              (NUTRITION_UNCOVERED_FORM_KEYS as readonly string[]).includes(key))
          ) {
            return obj;
          }

          if (key === 'nutritionInfo') {
            const dirtyNutritionInfo = this.getNutritionInfoChangesForChangeSet();

            if (!IsEmptyService.isEmpty(dirtyNutritionInfo)) {
              obj[key] = dirtyNutritionInfo;
            }

            return obj;
          }

          if (key === 'sodium') {
            if (!this.isSodiumRowActive()) {
              return obj;
            }

            if (!this.normalizeSodiumForPatch(control.value)) {
              obj[key] = null;
              return obj;
            }
          }

          if (key === 'selectedTagIds') {
            const controlValue = control.value;
            const selectedTagIds = Array.isArray(controlValue)
              ? controlValue
              : controlValue?.selectedTagIds || [];
            const { marketingTagIds, ueTaxCategoryIds } =
              this.splitSelectedTagIdsForChangeSet(selectedTagIds);

            obj.tags = {
              marketingTagIds: marketingTagIds.map((tag) => tag.id),
              ueTaxCategoryIds: ueTaxCategoryIds.map((tag) => tag.id),
            };

            return obj;
          }

          obj[key] =
            typeof control.value === 'string'
              ? control.value.trim()
              : trimCatalogItemFields(control.value);

          return obj;
        },
        {} as Record<string, any>,
      );

      if (Object.keys(dirtyFieldsObject).length) {
        // if the change is recent, we are adding the other changes to the itemChanges
        // object in order to keep them dirty (marking the correct field as pristine)
        Object.assign(changesForVersion, dirtyFieldsObject);
      }

      if (dirtyFieldsObject.nutritionInfo) {
        const catalogEntryId = clonedItem.catalogEntryId;
        const newlyRemovedFromDirty = Object.keys(dirtyFieldsObject.nutritionInfo).filter(
          (key) =>
            !(this.nutritionStaticKeys as readonly string[]).includes(key) &&
            !this.hasMeaningfulNutritionEntry(dirtyFieldsObject.nutritionInfo[key]),
        );

        if (newlyRemovedFromDirty.length) {
          this.addRemovedNutritionKeys(this.selectedVersion, catalogEntryId, newlyRemovedFromDirty);
        }

        const removedKeys = this.getRemovedNutritionKeys(this.selectedVersion, catalogEntryId);
        const effectiveNutritionInfo = this.buildEffectiveNutritionInfoForVersion(
          clonedItem.nutritionInfo,
          dirtyFieldsObject.nutritionInfo,
          removedKeys,
        );

        if (IsEmptyService.isEmpty(effectiveNutritionInfo)) {
          delete changesForVersion.nutritionInfo;
        } else {
          changesForVersion.nutritionInfo = effectiveNutritionInfo;
        }
      }

      if (isUncoveredPropertiesVersion) {
        UNCOVERED_CHANGE_SET_FIELDS.forEach((field) => {
          delete changesForVersion[field];
        });
        NUTRITION_UNCOVERED_FORM_KEYS.forEach((field) => {
          delete changesForVersion[field];
        });
      }

      delete (changesForVersion as Record<string, unknown>).selectedTagIds;

      delete versionData.selectedTagIds;
      const selectedTagIdsControl = this.form.get('selectedTagIds');
      if (selectedTagIdsControl) {
        const controlValue = selectedTagIdsControl.value;
        const selectedTagIds = Array.isArray(controlValue)
          ? controlValue
          : controlValue?.selectedTagIds || [];
        const { marketingTagIds, ueTaxCategoryIds } =
          this.splitSelectedTagIdsForChangeSet(selectedTagIds);
        versionData.marketingTagIds = marketingTagIds;
        versionData.ueTaxCategoryIds = ueTaxCategoryIds;
      }

      const currentDecoratorsMap =
        decorators?.reduce((obj, decorator) => {
          obj[`decorator_${decorator.key}_${decorator.field}`] = decorator;
          return obj;
        }, {}) || {};
      const versionDecoratorsMap =
        changesForVersion?.decorators?.reduce((obj, decorator) => {
          obj[`decorator_${decorator.key}_${decorator.field}`] = decorator;
          return obj;
        }, {}) || {};

      if (this.cachedVersion) {
        // checking if things have been deleted
        Object.keys(this.cachedVersion).forEach((key) => {
          const formForKey = this.form.get(key);
          const dirtyCheck = !formForKey || !formForKey?.dirty;

          if (!this.itemChanges[this.selectedVersion].hasOwnProperty(key) && dirtyCheck) {
            // if form is dirty, the user edited the field after reverting a change
            // => we don't delete the change
            deletedKeys.push(key);
          }
        });

        const cachedDecoratorsMap = (this.cachedVersion?.decorators || []).reduce(
          (obj, decorator) => {
            obj[`decorator_${decorator.key}_${decorator.field}`] = decorator;
            return obj;
          },
          {},
        );

        Object.keys(cachedDecoratorsMap).forEach((itemKey) => {
          if (!versionDecoratorsMap[itemKey]) {
            deletedKeys.push(itemKey);
          }
        });
      }

      (this.cachedVersion?.decorators || decorators || []).forEach((decorator) => {
        const itemKey = `decorator_${decorator.key}_${decorator.field}`;
        const alreadyDeleted = deletedKeys.indexOf(itemKey) >= 0;

        if (
          !versionDecoratorsMap?.[itemKey] &&
          !currentDecoratorsMap?.[itemKey] &&
          !alreadyDeleted
        ) {
          // making sure it is actually deleted from the current decorators
          deletedKeys.push(itemKey);
        } else {
          delete versionDecoratorsMap?.[itemKey];
        }
      });

      Object.keys(versionDecoratorsMap).forEach((itemKey) => {
        if (!currentDecoratorsMap[itemKey]) {
          // making sure it is actually deleted from the current decorators
          deletedKeys.push(itemKey);
        }
      });

      const diffObj: any = this.formatChangeSetToSave(versionData, clonedItem, upsellData);
      const { ...body } = diffObj;

      if (body.hasOwnProperty('sodium') && !this.normalizeSodiumForPatch(body.sodium)) {
        if (!deletedKeys.includes('sodium')) {
          deletedKeys.push('sodium');
        }
        delete body.sodium;
      }

      const changeSetSelected = this.changeSets.find(
        (changeSet) => changeSet.type === this.selectedVersion,
      );

      return this.createPatchBodyForUpsert(
        body,
        deletedKeys,
        entityType,
        clonedItem.catalogEntryId,
      ).pipe(
        mergeMap((patchBody) => {
          return this.changeSetsService
            .saveVersionItem(changeSetSelected.id, clonedItem.catalogEntryId, patchBody)
            .pipe(
              catchError((e) => {
                // catch patch error issues
                this.pageErrorMessage = this.translateService.instant(
                  'catalog.changeSets.changeSetSaveFailMsg',
                );
                return throwError(() => {
                  return { error: e };
                });
              }),
              mergeMap((response: PatchCatalogChangesResponse) => {
                if (response?.decorators.length) {
                  const imagesToUpload = response.decorators.reduce((obj, dec) => {
                    if (dec.field === 'images' && dec.multimediaUploadUrl) {
                      obj[`${dec.field}-${dec.key}`] = dec;
                    }
                    return obj;
                  }, {});

                  if (Object.keys(imagesToUpload).length) {
                    const obs = Object.keys(imagesToUpload).map((imageKey) =>
                      this.catalogImagesService.uploadImage(
                        imagesToUpload[imageKey].multimediaUploadUrl,
                        versionData.images[imagesToUpload[imageKey].key],
                      ),
                    );

                    // upload images
                    return forkJoin(obs).pipe(
                      catchError((e) => {
                        // catch files upload issues
                        this.pageErrorMessage = this.translateService.instant(
                          'catalog.changeSets.uploadFailMsg',
                        );
                        return throwError(() => {
                          return { error: e };
                        });
                      }),
                      mergeMap(() => {
                        const newPatchBody = DeepCloneService.clone(patchBody);

                        if (!newPatchBody.upsert.base) {
                          newPatchBody.upsert.base = {};
                        }

                        if (!newPatchBody.upsert.base?.images) {
                          newPatchBody.upsert.base.images = {};
                        }

                        const newDecorators = response.decorators.filter((dec) => {
                          if (dec.multimedia) {
                            newPatchBody.upsert.base.images[dec.key] = dec.value;
                          }

                          return !imagesToUpload[`${dec.field}-${dec.key}`];
                        });

                        newPatchBody.upsert.decorators = newDecorators;

                        return this.changeSetsService
                          .saveVersionItem(
                            changeSetSelected.id,
                            clonedItem.catalogEntryId,
                            newPatchBody,
                          )
                          .pipe(
                            map(() => {
                              this.changeSetsService.reloadChangeList.next(Date.now());
                            }),
                            catchError((e) => {
                              // catch patch error issues
                              this.pageErrorMessage = this.translateService.instant(
                                'catalog.changeSets.changeSetSaveFailMsg',
                              );
                              return throwError(() => {
                                return { error: e };
                              });
                            }),
                          );
                      }),
                    );
                  } else {
                    this.changeSetsService.reloadChangeList.next(Date.now());
                  }
                } else {
                  this.changeSetsService.reloadChangeList.next(Date.now());
                }

                return of({});
              }),
            );
        }),
      );
    }

    formatChangeSetToSave(versionData, publishedItem, upsellData) {
      const { caloricCount, modifierGroupContextData, ...remainingPublishData } = publishedItem;
      const { calories, caloricCountInput } = this.transformCaloricCount(caloricCount) || {};

      let transformedHiddenModifiers = [];

      if (modifierGroupContextData && modifierGroupContextData.length > 0) {
        transformedHiddenModifiers = this.transformHiddenModifiers(modifierGroupContextData);
      }

      if (versionData.modifierGroupContextData && versionData.modifierGroupContextData.length > 0) {
        versionData.modifierGroupContextData = this.getModifierGroupContextData(versionData);
        const newHiddenModifiers = this.transformHiddenModifiers(
          versionData.modifierGroupContextData,
        );
        versionData.modifierGroupContextData = versionData.modifierGroupContextData.map(
          (contextData) => {
            return {
              ...contextData,
              hiddenModifiers: newHiddenModifiers,
            };
          },
        );
      }

      if (versionData.alcohol?.volume) {
        versionData.alcohol.volume = parseFloat(versionData.alcohol.volume);
      }

      if (
        versionData.sodium?.value !== null &&
        versionData.sodium?.value !== undefined &&
        versionData.sodium?.value !== ''
      ) {
        versionData.sodium.value = parseFloat(versionData.sodium.value);
      }
      if (
        versionData.sodium?.lowerRange !== null &&
        versionData.sodium?.lowerRange !== undefined &&
        versionData.sodium?.lowerRange !== ''
      ) {
        versionData.sodium.lowerRange = parseFloat(versionData.sodium.lowerRange);
      }
      if (
        versionData.sodium?.higherRange !== null &&
        versionData.sodium?.higherRange !== undefined &&
        versionData.sodium?.higherRange !== ''
      ) {
        versionData.sodium.higherRange = parseFloat(versionData.sodium.higherRange);
      }

      const publishDataToCompare = {
        ...remainingPublishData,
        calories,
        caloricCountInput,
        modifierGroupContextData,
      };

      if (modifierGroupContextData) {
        publishDataToCompare.modifierGroupIds = modifierGroupContextData.map(
          (d) => d.modifierGroupId,
        );
      }

      if (upsellData) {
        versionData.upsellData = upsellData;
        publishDataToCompare.upsellData = this.cachedBeforeVersionChange?.form?.upsellData;
      }

      if (modifierGroupContextData && modifierGroupContextData.length > 0) {
        publishDataToCompare.modifierGroupContextData = modifierGroupContextData.map(
          (contextData) => {
            return {
              ...contextData,
              hiddenModifiers: transformedHiddenModifiers,
            };
          },
        );
      }

      delete versionData.selectedTagIds;
      const publishedTags = this.getPublishedTagBaselineForChangeSet();
      publishDataToCompare.marketingTagIds = publishedTags.marketingTagIds;
      publishDataToCompare.ueTaxCategoryIds = publishedTags.ueTaxCategoryIds;
      delete publishDataToCompare.selectedTagIds;

      const differenceResult: any = this.updateChangeSet(versionData, publishDataToCompare);
      const isModifierGroup = publishedItem?.entityType === CatalogLookupType.modifierGroup;
      const modifierGroupChanges = this.itemChanges?.[this.selectedVersion]?.modifierGroup;
      const quantityMatchesPublished = (field: 'minimumQuantity' | 'maximumQuantity') => {
        const customizer = (item1, item2) => {
          const oneIsNumber = typeof item1 === 'number' || typeof item2 === 'number';

          if (oneIsNumber && `${item1}` === `${item2}`) {
            return true;
          }

          if (
            (item1 === undefined || item1 === null || item1 === '') &&
            (item2 === undefined || item2 === null || item2 === '')
          ) {
            return true;
          }
        };

        return IsEqualWithService.isEqualWith(
          versionData[field],
          publishDataToCompare[field],
          customizer,
        );
      };
      const isPendingModifierGroupQuantity = (field: 'minimumQuantity' | 'maximumQuantity') => {
        return !!(
          modifierGroupChanges && Object.prototype.hasOwnProperty.call(modifierGroupChanges, field)
        );
      };

      (['minimumQuantity', 'maximumQuantity'] as const).forEach((field) => {
        if (
          isModifierGroup &&
          differenceResult.hasOwnProperty(field) &&
          !isPendingModifierGroupQuantity(field) &&
          quantityMatchesPublished(field)
        ) {
          delete differenceResult[field];
        }

        if (differenceResult.hasOwnProperty(field)) {
          return;
        }

        if (!isModifierGroup) {
          differenceResult[field] = versionData[field];
          return;
        }

        if (isPendingModifierGroupQuantity(field)) {
          differenceResult[field] = versionData[field];
          return;
        }

        if (!modifierGroupChanges && versionData[field] === null) {
          differenceResult[field] = versionData[field];
        }
      });

      const isPendingModifierGroupSku = () => {
        return !!(
          modifierGroupChanges && Object.prototype.hasOwnProperty.call(modifierGroupChanges, 'sku')
        );
      };
      const skuMatchesPublished = () => {
        const customizer = (item1, item2) => {
          if (
            (item1 === undefined || item1 === null || item1 === '') &&
            (item2 === undefined || item2 === null || item2 === '')
          ) {
            return true;
          }
        };

        return IsEqualWithService.isEqualWith(
          versionData.sku,
          publishDataToCompare.sku,
          customizer,
        );
      };

      if (
        isModifierGroup &&
        differenceResult.hasOwnProperty('sku') &&
        !isPendingModifierGroupSku() &&
        skuMatchesPublished()
      ) {
        delete differenceResult.sku;
      }

      if (
        isModifierGroup &&
        !differenceResult.hasOwnProperty('sku') &&
        isPendingModifierGroupSku()
      ) {
        differenceResult.sku = versionData.sku;
      }

      return differenceResult;
    }

    transformHiddenModifiers(modifierGroupContextData) {
      const hiddenModifiers = modifierGroupContextData
        .flatMap((contextData) => contextData.hiddenModifiers)
        .filter((modifier) => modifier.hidden)
        .map((modifier) => modifier.modifierId);

      return hiddenModifiers;
    }

    toNullableNumber(rawValue: any): number | null {
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return null;
      }

      if (typeof rawValue === 'number') {
        return Number.isFinite(rawValue) ? rawValue : null;
      }

      if (typeof rawValue === 'string') {
        const cleaned = rawValue.trim().replace(/^"+|"+$/g, '');
        if (!cleaned.length) {
          return null;
        }
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : null;
      }

      return null;
    }

    sanitizeNutritionInfoForPatch(nutritionInfo: any): Record<string, any> {
      if (!nutritionInfo || typeof nutritionInfo !== 'object') {
        return {};
      }

      const normalized: Record<string, any> = {};

      Object.keys(nutritionInfo).forEach((key) => {
        if (!this.nutritionInfoAllowedKeys.has(key)) {
          return;
        }

        const value = nutritionInfo[key];

        if (key === 'additives') {
          if (typeof value === 'string') {
            normalized.additives = value;
          }
          return;
        }

        if (key === 'servingSize') {
          if (typeof value === 'string') {
            normalized.servingSize = value;
          } else if (value != null) {
            normalized.servingSize = `${value}`;
          }
          return;
        }

        if (key === 'numberOfServings') {
          const servings = this.toNullableNumber(value);
          if (servings !== null) {
            normalized.numberOfServings = servings;
          }
          return;
        }

        if (!value || typeof value !== 'object') {
          return;
        }

        const lowerRange = this.toNullableNumber(value.lowerRange);
        const higherRange = this.toNullableNumber(value.higherRange);
        const unit = typeof value.unit === 'string' ? value.unit : null;
        const normalizedValue: Record<string, any> = {};

        if (lowerRange !== null) {
          normalizedValue.lowerRange = lowerRange;
        }
        if (higherRange !== null) {
          normalizedValue.higherRange = higherRange;
        }
        if (unit && this.nutritionUnitsAllowed.has(unit)) {
          normalizedValue.unit = unit;
        }

        if (Object.keys(normalizedValue).length) {
          normalized[key] = normalizedValue;
        }
      });

      return normalized;
    }

    normalizeCaloriesForPatch(payload: any): Record<string, any> | null {
      if (!payload || typeof payload !== 'object') {
        return null;
      }

      const normalized: Record<string, any> = {};
      const calories = this.toNormalizedCaloriesValue(payload.calories);

      if (calories !== null) {
        normalized.calories = calories;
      }

      const caloricCountInput = this.normalizeCaloricCountInputForPatch(payload.caloricCountInput);
      if (caloricCountInput) {
        normalized.caloricCountInput = caloricCountInput;
      }

      return Object.keys(normalized).length ? normalized : null;
    }

    normalizeCaloricCountInputForPatch(caloricCountInput: any): Record<string, any> | null {
      if (!caloricCountInput || typeof caloricCountInput !== 'object') {
        return null;
      }

      const normalized: Record<string, any> = {};
      const caloricUnit =
        typeof caloricCountInput.caloricUnit === 'string'
          ? caloricCountInput.caloricUnit.trim()
          : null;

      if (caloricUnit && this.nutritionUnitsAllowed.has(caloricUnit)) {
        normalized.caloricUnit = caloricUnit;
      }

      const lowerRange = this.toNullableNumber(caloricCountInput.lowerRange);
      const higherRange = this.toNullableNumber(caloricCountInput.higherRange);
      const servings = this.toNullableNumber(caloricCountInput.servings);

      if (lowerRange !== null) {
        normalized.lowerRange = lowerRange;
      }
      if (higherRange !== null) {
        normalized.higherRange = higherRange;
      }
      if (servings !== null) {
        normalized.servings = servings;
      }

      return Object.keys(normalized).length ? normalized : null;
    }

    normalizeSodiumForPatch(sodium: any): Record<string, any> | null {
      if (!sodium || typeof sodium !== 'object') {
        return null;
      }

      const lowerRange = this.toNullableNumber(sodium.lowerRange);
      const higherRange = this.toNullableNumber(sodium.higherRange);
      const unit = typeof sodium.unit === 'string' ? sodium.unit.trim() : null;
      const hasValidUnit = !!unit && this.nutritionUnitsAllowed.has(unit);

      if (!hasValidUnit) {
        return null;
      }

      const normalized: Record<string, any> = { unit };

      if (lowerRange !== null) {
        normalized.lowerRange = lowerRange;
      }
      if (higherRange !== null) {
        normalized.higherRange = higherRange;
      }

      return normalized;
    }

    toNormalizedCaloriesValue(rawValue: any): string | null {
      if (rawValue === null || rawValue === undefined) {
        return null;
      }

      const normalized = `${rawValue}`.trim().replace(/^"+|"+$/g, '');
      return normalized.length ? normalized : null;
    }

    getRemovedNutritionKeysCacheKey(version: ChangeSetItemType, catalogEntryId: string): string {
      return `${version}:${catalogEntryId}`;
    }

    getRemovedNutritionKeys(version: ChangeSetItemType, catalogEntryId: string): string[] {
      if (!catalogEntryId) {
        return [];
      }

      const cacheKey = this.getRemovedNutritionKeysCacheKey(version, catalogEntryId);
      const changesForVersion = this.itemChanges?.[version] as
        | (FormattedChangesForItem & { removedNutritionKeys?: string[] })
        | undefined;

      return (
        this.removedNutritionKeysByItemVersion[cacheKey] ||
        changesForVersion?.removedNutritionKeys ||
        []
      );
    }

    setRemovedNutritionKeys(
      version: ChangeSetItemType,
      catalogEntryId: string,
      keys: string[],
    ): void {
      if (!catalogEntryId) {
        return;
      }

      const cacheKey = this.getRemovedNutritionKeysCacheKey(version, catalogEntryId);
      const uniqueKeys = UniqService.uniq(keys);

      this.removedNutritionKeysByItemVersion[cacheKey] = uniqueKeys;

      const changesForVersion = this.itemChanges?.[version] as
        | (FormattedChangesForItem & { removedNutritionKeys?: string[] })
        | undefined;

      if (changesForVersion) {
        changesForVersion.removedNutritionKeys = uniqueKeys;
      }
    }

    addRemovedNutritionKeys(
      version: ChangeSetItemType,
      catalogEntryId: string,
      keys: string[],
    ): void {
      const current = this.getRemovedNutritionKeys(version, catalogEntryId);
      this.setRemovedNutritionKeys(version, catalogEntryId, [...current, ...keys]);
    }

    removeRemovedNutritionKeys(
      version: ChangeSetItemType,
      catalogEntryId: string,
      keys: string[],
    ): void {
      const keysToRemove = new Set(keys);
      const current = this.getRemovedNutritionKeys(version, catalogEntryId).filter(
        (key) => !keysToRemove.has(key),
      );
      this.setRemovedNutritionKeys(version, catalogEntryId, current);
    }

    hasMeaningfulNutritionEntry(value: any): boolean {
      if (!value || typeof value !== 'object') {
        return false;
      }

      return (
        this.toNullableNumber(value.lowerRange) !== null ||
        this.toNullableNumber(value.higherRange) !== null ||
        (typeof value.unit === 'string' && !!value.unit.trim())
      );
    }

    mergeNutritionInfoForChangeSetView(
      published: Record<string, any> | undefined,
      changes: Record<string, any> | undefined,
      removedKeys: string[] = [],
    ): Record<string, any> {
      const publishedInfo = published || {};
      const changesInfo = changes || {};
      const removedSet = new Set(removedKeys);
      const merged: Record<string, any> = {};

      this.nutritionStaticKeys.forEach((key) => {
        if (changesInfo[key] !== undefined) {
          merged[key] = changesInfo[key];
        } else if (publishedInfo[key] !== undefined) {
          merged[key] = publishedInfo[key];
        }
      });

      const allNutrientKeys = new Set([
        ...Object.keys(publishedInfo).filter(
          (key) => !(this.nutritionStaticKeys as readonly string[]).includes(key),
        ),
        ...Object.keys(changesInfo).filter(
          (key) => !(this.nutritionStaticKeys as readonly string[]).includes(key),
        ),
      ]);

      allNutrientKeys.forEach((key) => {
        if (removedSet.has(key)) {
          return;
        }

        if (key in changesInfo) {
          if (this.hasMeaningfulNutritionEntry(changesInfo[key])) {
            merged[key] = changesInfo[key];
          }
          return;
        }

        if (this.hasMeaningfulNutritionEntry(publishedInfo[key])) {
          merged[key] = publishedInfo[key];
        }
      });

      return merged;
    }

    buildEffectiveNutritionInfoForVersion(
      published: Record<string, any> | undefined,
      dirtyChanges: Record<string, any> | undefined,
      removedKeys: string[] = [],
    ): Record<string, any> {
      const removedSet = new Set(removedKeys);
      const dirtyInfo = dirtyChanges || {};

      Object.keys(dirtyInfo).forEach((key) => {
        if ((this.nutritionStaticKeys as readonly string[]).includes(key)) {
          return;
        }

        if (!this.hasMeaningfulNutritionEntry(dirtyInfo[key])) {
          removedSet.add(key);
        }
      });

      return this.mergeNutritionInfoForChangeSetView(published, dirtyInfo, Array.from(removedSet));
    }

    buildNutritionInfoPatchForForm(nutritionInfoChanges: Record<string, any>): Record<string, any> {
      if (!nutritionInfoChanges || typeof nutritionInfoChanges !== 'object') {
        return nutritionInfoChanges;
      }

      return Object.keys(nutritionInfoChanges).reduce((acc, key) => {
        const value = nutritionInfoChanges[key];
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
          acc[key] = value;
          return acc;
        }

        // patchValue keeps missing nested controls unchanged; explicitly clear omitted ranges
        // so refreshed scheduled/draft view does not retain stale published values.
        acc[key] = {
          ...value,
          lowerRange: value.hasOwnProperty('lowerRange') ? value.lowerRange : null,
          higherRange: value.hasOwnProperty('higherRange') ? value.higherRange : null,
        };

        return acc;
      }, {});
    }

    buildSodiumPatchForForm(sodiumChanges: Record<string, any> | null): Record<string, any> | null {
      if (!sodiumChanges || typeof sodiumChanges !== 'object') {
        return sodiumChanges;
      }

      return {
        ...sodiumChanges,
        value: sodiumChanges.hasOwnProperty('value') ? sodiumChanges.value : null,
        lowerRange: sodiumChanges.hasOwnProperty('lowerRange') ? sodiumChanges.lowerRange : null,
        higherRange: sodiumChanges.hasOwnProperty('higherRange') ? sodiumChanges.higherRange : null,
      };
    }

    transformCaloricCount(caloricCount: any): any {
      if (!caloricCount) {
        return null;
      }

      const { servings, lowerRangeInKcal, higherRangeInKcal } = caloricCount;
      const calories = lowerRangeInKcal || higherRangeInKcal;
      const caloricCountInput = {
        caloricUnit: this.brand.defaultCaloricUnit || 'kcal',
        lowerRange: lowerRangeInKcal ? parseFloat(lowerRangeInKcal) : lowerRangeInKcal,
        higherRange: higherRangeInKcal ? parseFloat(higherRangeInKcal) : higherRangeInKcal,
        servings: servings,
      };

      return { calories, caloricCountInput };
    }

    updateChangeSet(obj1, obj2) {
      const diff = {};

      for (const key in obj1) {
        if (obj1.hasOwnProperty(key)) {
          const value1 = obj1[key];
          const value2 = obj2[key];
          const customizer = (item1, item2) => {
            const oneIsNumber = typeof item1 === 'number' || typeof item2 === 'number';

            if (oneIsNumber && `${item1}` === `${item2}`) {
              return true;
            }

            if (
              (item1 === undefined || item1 === null || item1 === '') &&
              (item2 === undefined || item2 === null || item2 === '')
            ) {
              return true;
            }
          };

          if (!IsEqualWithService.isEqualWith(value1, value2, customizer)) {
            diff[key] = value1;
          }
        }
      }

      // Check for keys in obj2 that are not in obj1
      for (const key in obj2) {
        if (obj2.hasOwnProperty(key) && !obj1.hasOwnProperty(key)) {
          diff[key] = obj2[key];
        }
      }

      const changesForVersion = this.itemChanges?.[this.selectedVersion];

      // Check for keys in already made changes that are not in diff
      for (const key in changesForVersion) {
        if (changesForVersion.hasOwnProperty(key) && !diff.hasOwnProperty(key)) {
          diff[key] = obj1[key];
        }
      }

      return diff;
    }

    hasFieldChanged(
      formField: string,
      fullPath: string,
      versionToCompare: string,
      itemChanges: FormattedChangesForItem,
    ): boolean {
      const formForControl = this.form.get(formField);
      const pathToField = fullPath || formField;
      const sameVersion = versionToCompare === this.selectedVersion;

      return !!GetService.get(itemChanges, pathToField) || (sameVersion && formForControl?.dirty);
    }

    setChangeSets(changeSets: ChangeSetItem[]) {
      this.changeSets = changeSets;
    }

    onRevertSpecificChange(path: string, selectedItem: any) {
      const paths = path.split('.');
      const cachedVersionType = this.selectedVersion;

      this.changeSetsService.isResetting = true;

      if (!this.cachedVersion) {
        // caching changes since we're going to change stuff
        this.cachedVersion = DeepCloneService.clone(this.itemChanges[cachedVersionType]);
      }

      const changesForItem = this.itemChanges?.[cachedVersionType] || {};
      const dirtyFieldsObject: any = Object.keys(this.form.controls).reduce((obj, key) => {
        const control = this.form.controls[key];
        if (!control.dirty) {
          return obj;
        }

        if (key === 'nutritionInfo' && paths[0] === 'nutritionInfo' && paths[1]) {
          const dirtyNutritionInfo = this.getDirtyNutritionInfoChanges(paths[1]);

          if (!IsEmptyService.isEmpty(dirtyNutritionInfo)) {
            obj[key] = dirtyNutritionInfo;
          }

          return obj;
        }

        obj[key] = control.value;

        return obj;
      }, {});

      if (paths[2] === 'images') {
        const [, decKey, decField] = paths;

        // we remove the image from the decorators (image is not newly added)
        changesForItem.decorators =
          changesForItem.decorators?.filter(
            (decorator) => !(decorator.key === decKey && decorator.field === decField),
          ) || [];

        const formControl = this.getChangeSetImageControl(paths[1]);
        const previousValue = this.getPublishedImageBaselineValue(paths[1]);

        if (formControl) {
          if (isLocalizedImageKey(paths[1])) {
            const currentMap = { ...(formControl.value || {}) };
            if (previousValue == null) {
              delete currentMap[paths[1]];
            } else {
              currentMap[paths[1]] = previousValue;
            }
            formControl.setValue(currentMap);
          } else {
            formControl.setValue(previousValue);
          }
          formControl.markAsPristine();
        }

        this.form.markAsDirty();
        this.changeSetsService.isResetting = false;

        return;
      }

      if (Object.keys(dirtyFieldsObject).length || paths[0] === 'decorators') {
        // if the change is recent, we are adding the other changes to the itemChanges
        // object in order to keep them dirty (marking the correct field as pristine)
        Object.assign(changesForItem, dirtyFieldsObject);

        if (dirtyFieldsObject.decorators) {
          const prevDecoratorMap =
            (this.cachedBeforeVersionChange?.decorators || []).reduce((obj, decorator) => {
              obj[`decorator_${decorator.key}_${decorator.field}`] = decorator;
              return obj;
            }, {}) || {};

          changesForItem.decorators = changesForItem.decorators.filter((decorator) => {
            const key = `decorator_${decorator.key}_${decorator.field}`;

            if (!prevDecoratorMap.hasOwnProperty(key)) {
              // if decorator was not previously present, but it has a value now, make it dirty
              return !!decorator.value;
            }

            // if decorator was present before and has a different value now, we make it dirty as well
            return prevDecoratorMap[key].value !== decorator.value;
          });
        }
      }

      if (path === 'nutritionItems') {
        delete changesForItem.nutritionInfo;
        delete changesForItem.sodium;

        this.setCurrentVersion({ version: ChangeSetItemType.PUBLISHED, selectedItem }, true);
        this.setCurrentVersion({ version: cachedVersionType, selectedItem });
        this.nutritionInfo?.populateNutritionInfoFromCurrentSource();

        this.form.markAsDirty();
        this.changeSetsService.isResetting = false;
        return;
      }

      // we are resetting to initial version
      this.setCurrentVersion({ version: ChangeSetItemType.PUBLISHED, selectedItem }, true);

      if (paths.length > 1) {
        const [firstKey, decKey, decField] = paths;

        if (firstKey === 'decorators') {
          changesForItem.decorators =
            changesForItem.decorators?.filter(
              (decorator) => !(decorator.key === decKey && decorator.field === decField),
            ) || [];
        } else {
          const deleteDirectly = ['product'];

          UnsetService.unset(changesForItem, path);

          if (
            paths[0] === 'modifierGroup' &&
            (paths[1] === 'minimumQuantity' || paths[1] === 'maximumQuantity' || paths[1] === 'sku')
          ) {
            delete changesForItem[paths[1]];
          }

          // if full entity type is empty after this change, remove everything
          if (
            deleteDirectly.includes(paths[0]) ||
            IsEmptyService.isEmpty(changesForItem[paths[0]])
          ) {
            // product only has modifierGroupIds and modifierGroupContextData
            delete changesForItem[paths[0]];
          }
        }
      } else {
        delete changesForItem[paths[0]];
      }

      // setting draft version again
      this.setCurrentVersion({ version: cachedVersionType, selectedItem });

      if (paths[0] === 'nutritionInfo' && paths[1]) {
        const publishedNutritionInfo = selectedItem?.nutritionInfo?.[paths[1]];
        const hasChangeForNutritionInfo = !!GetService.get(changesForItem, path);

        if (!publishedNutritionInfo && !hasChangeForNutritionInfo && this.nutritionInfo) {
          this.nutritionInfo.activeNutritionKeys = this.nutritionInfo.activeNutritionKeys.filter(
            (key) => key !== paths[1],
          );
          this.nutritionInfo.buildNutritionToggles();
        }
      }

      // marking form controls as touched, so we can save changes
      this.form.markAsDirty();
      this.changeSetsService.isResetting = false;
    }

    appendModifierGroupChangesToProduct() {
      this.showModifierGroupWarning = false;

      if (
        !this.selectedProduct ||
        !this.selectedProduct.modifierGroupIds?.length ||
        !this.changesForChangeSet ||
        !this.itemChanges
      ) {
        return;
      }

      if (this.cachedLookupMap) {
        this.modifierGroupLookupMap = this.cachedLookupMap;
        this.cachedLookupMap = undefined;
      }

      Object.keys(this.itemChanges).forEach((versionType) => {
        const changesForItem = this.itemChanges[versionType];
        const changesForType = this.changesForChangeSet[versionType];

        const newLookupMap = DeepCloneService.clone(this.modifierGroupLookupMap);

        this.selectedProduct.modifierGroupIds.forEach((modGroupId) => {
          const modGroupChanges = changesForType[modGroupId];

          if (modGroupChanges) {
            const formattedChanges = {};

            modGroupChanges.forEach((modChange) => {
              Object.assign(formattedChanges, modChange.targetValue);
            });

            Object.assign(newLookupMap[modGroupId], formattedChanges);
          }
        });

        if (!IsEqualService.isEqual(newLookupMap, this.modifierGroupLookupMap)) {
          this.showModifierGroupWarning = false;

          // if we actually have a difference we need to make sure that the itemChanges object has
          // product changes (if they are not there already)
          if (!changesForItem.product) {
            this.showModifierGroupWarning = true;
            // we add some data that actually has no change to the product
            // used only to show to the user that there is a change
            changesForItem.product = {
              modifierGroupIds: this.selectedProduct.modifierGroupIds,
              modifierGroupContextData: this.selectedProduct.modifierGroupContextData,
            };
          } else if (!changesForItem.product.modifierGroupIds) {
            this.showModifierGroupWarning = true;
            // if we already have product changes, but no modifierGroup changes, we add the data here as well
            Object.assign(changesForItem.product, {
              modifierGroupIds: this.selectedProduct.modifierGroupIds,
              modifierGroupContextData: this.selectedProduct.modifierGroupContextData,
            });
          }

          this.lookupMapForChangeSetVersion[versionType] = newLookupMap;
        }
      });
    }

    setItemChanges(newValue: {
      itemChanges: ItemChanges;
      selectedItem: any;
      changesLoaded?: boolean;
    }) {
      const { itemChanges, selectedItem, changesLoaded } = newValue;

      this.itemChanges = itemChanges || {};

      if (changesLoaded === false) {
        this.itemChangesCatalogEntryId = undefined;
      } else {
        this.itemChangesCatalogEntryId = selectedItem?.catalogEntryId;
      }

      this.appendModifierGroupChangesToProduct();

      const itemChanged =
        selectedItem?.catalogEntryId !== this.currentItemForChangeSet?.catalogEntryId;
      const sameItemReloaded = !itemChanged && selectedItem !== this.currentItemForChangeSet;

      if (itemChanged) {
        delete this.cachedVersion;
        this.cachedBeforeVersionChange = {};
        this.cachedAfterVersionChange = {};
      } else if (sameItemReloaded) {
        // Draft save clones the item (reloadKey). Keep the published form snapshot
        // but drop revert snapshots so GET itemChanges stay authoritative.
        delete this.cachedVersion;
        this.cachedAfterVersionChange = {};
      }

      this.currentItemForChangeSet = selectedItem;
      this.versionChangeRequestId++;
      this.versionChangeTimerSubscription.unsubscribe();

      if (changesLoaded === false) {
        return;
      }

      this.applyDecoratorsForCurrentItem();

      const hasLoadedChanges = !!itemChanges && Object.keys(itemChanges).length > 0;

      if (this.selectedVersion !== ChangeSetItemType.PUBLISHED && hasLoadedChanges) {
        const requestId = this.versionChangeRequestId;
        // checking every 0.5 seconds to see if flags for loading are false
        this.versionChangeTimerSubscription = timer(500, 500)
          .pipe(
            takeWhile(() => {
              return !!this.loadingFields.find((field) => this[field]);
            }, true), // take while some loading fields are true
          )
          .subscribe({
            complete: () => {
              if (requestId !== this.versionChangeRequestId) {
                return;
              }

              this.setCurrentVersion({ version: this.selectedVersion, selectedItem });
            },
          });
      }
    }

    mergeDecoratorsWithChanges(
      currentDecorators: Decorator[] = [],
      decoratorChanges: Decorator[] = [],
    ): Decorator[] {
      const byKey: { [itemKey: string]: Decorator } = {};

      (currentDecorators || []).forEach((decorator) => {
        byKey[`${decorator.key}_${decorator.field}`] = decorator;
      });

      (decoratorChanges || []).forEach((change) => {
        const itemKey = `${change.key}_${change.field}`;
        byKey[itemKey] = {
          ...byKey[itemKey],
          ...change,
        };
      });

      return Object.values(byKey);
    }

    formatChangeSetDecorators(obj, decoratorChanges: any[]) {
      const newDecorators = this.mergeDecoratorsWithChanges(this.decorators, decoratorChanges);

      this.imageDecorators = [];
      this.imageFormValues = {};

      const newImageFormValues = {};

      for (const item of newDecorators) {
        if (item.field === 'images') {
          this.imageDecorators.push(item);
          newImageFormValues[item.key] = item.value;
          this.imageFormValues[item.key] = item.value;
        }
      }

      obj.images = { ...obj.images, ...newImageFormValues };
      obj.decorators = newDecorators;
    }

    restorePublishedVersion(version: ChangeSetItemType, selectedItem: any) {
      // reversing things to initial changes
      this.handleCancel();

      // trying to restore specific form values
      if (this.cachedBeforeVersionChange?.form) {
        this.form.patchValue(this.cachedBeforeVersionChange.form);
      }

      if (this.cachedBeforeVersionChange?.decorators) {
        this.imageDecorators = [];

        for (const item of this.cachedBeforeVersionChange?.decorators) {
          if (item.field === 'images') {
            this.imageDecorators.push(item);
            this.imageFormValues[item.key] = item.value;
          }
        }
      }

      this.cachedBeforeVersionChange = {};
    }

    reloadItemList() {
      if (this.reloadItemFunction) {
        this.reloadItemFunction();
      }
    }

    setAllChanges(changes: { [key: string]: ChangesListResponse }) {
      this.changesForChangeSet = changes;
      this.appendModifierGroupChangesToProduct();
    }

    updateUncoveredPropertiesReadOnlyFlag(version: ChangeSetItemType): void {
      if (this.catalogItemsService) {
        this.catalogItemsService.isUncoveredPropertiesReadOnly =
          version === ChangeSetItemType.DRAFT || version === ChangeSetItemType.SCHEDULED;
      }
    }

    setCurrentVersion(
      newValue: { version: ChangeSetItemType; selectedItem: any },
      keepCache = false,
    ) {
      const { version, selectedItem } = newValue;
      this.updateUncoveredPropertiesReadOnlyFlag(version);
      const changesForItem = this.itemChanges?.[version];

      if (this.cachedLookupMap) {
        this.modifierGroupLookupMap = this.cachedLookupMap;
        this.cachedLookupMap = undefined;
      }

      if (this.selectedVersion === ChangeSetItemType.PUBLISHED) {
        const upsellData = this.form.get('upsellData')?.value;

        if (version !== ChangeSetItemType.PUBLISHED) {
          // resetting before switching to draft or scheduled
          this.handleCancel();
        }

        const beforeVersionData = DeepCloneService.clone(this.form.value);

        if (upsellData) {
          Object.assign(beforeVersionData, { upsellData });
        }

        this.cachedBeforeVersionChange.form = beforeVersionData;
        this.cachedBeforeVersionChange.decorators = DeepCloneService.clone(this.decorators);
      }

      let shouldResetFirst = false;

      if (
        this.selectedVersion !== ChangeSetItemType.PUBLISHED &&
        version !== ChangeSetItemType.PUBLISHED &&
        this.selectedVersion !== version
      ) {
        // Reset to published only when switching between draft and scheduled
        // so we do not keep the previous version's edits. Stay on the same
        // version when switching products — otherwise handleCancel overwrites
        // the new item's draft values with published.
        shouldResetFirst = true;
      }

      if (this.selectedVersion && (version === ChangeSetItemType.PUBLISHED || shouldResetFirst)) {
        if (this.cachedVersion && !keepCache) {
          // if we made changes to previous version, but they were not saved
          // we need to revert them
          this.itemChanges[this.selectedVersion] = this.cachedVersion;
          delete this.cachedVersion;
        }

        this.selectedVersion = version;
        this.restorePublishedVersion(version, selectedItem);
        this.form.markAsPristine();

        if (!shouldResetFirst) {
          return;
        } else {
          // the above reset deletes this, so we need to restore the changes
          this.cachedBeforeVersionChange.form = DeepCloneService.clone(this.form.value);
          this.cachedBeforeVersionChange.decorators = DeepCloneService.clone(this.decorators);
        }
      }

      this.selectedVersion = version;
      this.pruneImageClearsWithoutPublishedImage();

      if (this.itemChanges && changesForItem) {
        const directAssignPaths = [
          'product',
          'productGroup',
          'modifierGroup',
          'modifier',
          'composite',
          'images',
        ];
        const valuesToPatch = Object.keys(changesForItem).reduce((obj, key) => {
          if (directAssignPaths.includes(key)) {
            if (key === 'product') {
              const newChanges: FormChangeForVersion = changesForItem[key];
              if (!newChanges || typeof newChanges !== 'object' || Array.isArray(newChanges)) {
                return obj;
              }

              if (this.lookupMapForChangeSetVersion[version]) {
                this.cachedLookupMap = this.modifierGroupLookupMap;
                this.modifierGroupLookupMap = this.lookupMapForChangeSetVersion[version];
              }

              newChanges.modifierGroupContextData = this.getModifierGroupContextData(newChanges);
              Object.assign(obj, newChanges);
            }

            if (key === 'composite') {
              const newChanges: FormChangeForVersion = changesForItem[key];
              if (!newChanges || typeof newChanges !== 'object' || Array.isArray(newChanges)) {
                return obj;
              }

              Object.assign(obj, newChanges);
            }

            if (key === 'modifierGroup') {
              const modifierKeys = ['modifierQuantities', 'hiddenModifiers', 'modifierIds'];
              const modifiersObj = PickService.pick(changesForItem[key], modifierKeys);

              modifierKeys.forEach((key) => {
                const notPresent = !modifiersObj.hasOwnProperty(key);
                const previousValue = this.cachedBeforeVersionChange?.form?.modifiers?.[key];

                if (notPresent && previousValue) {
                  modifiersObj[key] = previousValue;
                }
              });

              const newChanges: FormChangeForVersion = {
                ...OmitService.omit(changesForItem[key], modifierKeys),
                modifiers: modifiersObj,
              };

              Object.assign(obj, newChanges);
            }

            if (key === 'productGroup') {
              const newChanges: FormChangeForVersion = changesForItem[key];

              Object.assign(obj, newChanges);
            }
          }

          if (['name', 'title', 'description', 'alcohol', 'images'].includes(key)) {
            obj[key] = changesForItem[key];
          }

          if (key === 'sodium') {
            (obj as any).sodium = this.buildSodiumPatchForForm(changesForItem.sodium);
          }

          if (key === 'calories') {
            obj.calories = changesForItem.calories.calories;
            obj.caloricCountInput = changesForItem.calories.caloricCountInput;
          }

          if (key === 'nutritionInfo') {
            obj.nutritionInfo = this.buildNutritionInfoPatchForForm(changesForItem.nutritionInfo);
          }

          if (key === 'tags') {
            const availableTagsMap = this.availableTags.reduce((obj, tag) => {
              obj[tag.id] = tag;
              return obj;
            }, {});

            const selectedTagIds: Tag[] = [];

            if (changesForItem.tags?.marketingTagIds) {
              selectedTagIds.push(
                ...changesForItem.tags.marketingTagIds
                  .map((tagId) => availableTagsMap[tagId])
                  .filter(Boolean),
              );
            }

            if (changesForItem.tags?.ueTaxCategoryIds) {
              selectedTagIds.push(
                ...changesForItem.tags.ueTaxCategoryIds
                  .map((tagId) => availableTagsMap[tagId])
                  .filter(Boolean),
              );
            }

            if (selectedTagIds.length) {
              obj.selectedTagIds = selectedTagIds;
            }

            if (changesForItem.tags?.dietaryTagIds) {
              obj.dietaryTagIds =
                changesForItem.tags.dietaryTagIds.map((tagId) => ({ tagId: tagId })) || [];
            }

            if (changesForItem.tags?.allergyTagIds) {
              obj.allergyTagIds = changesForItem.tags.allergyTagIds;
            }
          }

          if (key === 'upsell') {
            obj.upsellData = changesForItem.upsell;
          }

          if (key === 'decorators') {
            const decoratorChanges = changesForItem[key] || [];

            this.formatChangeSetDecorators(obj, decoratorChanges);
          }

          return obj;
        }, {} as FormChangeForVersion);

        this.form.patchValue(valuesToPatch);

        this.form.markAsPristine();

        const maximumQuantity = this.form.get('maximumQuantity');
        const minimumQuantity = this.form.get('minimumQuantity');

        if (maximumQuantity) {
          maximumQuantity.updateValueAndValidity();
        }

        if (minimumQuantity) {
          minimumQuantity.updateValueAndValidity();
        }

        const upsellData = this.form.get('upsellData')?.value;
        const afterVersionData = DeepCloneService.clone(this.form.value);

        if (upsellData) {
          Object.assign(afterVersionData, { upsellData });
        }

        this.cachedAfterVersionChange.form = afterVersionData;
        this.cachedAfterVersionChange.decorators = DeepCloneService.clone(this.decorators);
      }
    }

    updateItemInListAndNavigateIfNew<T extends { catalogEntryId: string }>(
      selectedItem: T | null | undefined,
      itemList: T[],
      isNew: boolean,
      router: Router,
      route: ActivatedRoute,
    ): T[] {
      if (!selectedItem) return itemList;

      const idx = itemList.findIndex((item) => item.catalogEntryId === selectedItem.catalogEntryId);

      if (idx !== -1) {
        itemList[idx] = DeepCloneService.clone(selectedItem);
        itemList = [...itemList]; // trigger change detection
      }

      if (isNew) {
        router.navigate(['../' + selectedItem.catalogEntryId], {
          relativeTo: route,
          state: { data: selectedItem },
          replaceUrl: true,
        });
      }

      return itemList;
    }
  };
